import React from "react";
import { Card } from "flowbite-react";

interface IntegrationBlockSkeletonProps {
  count?: number;
}

const IntegrationBlockSkeleton: React.FC<IntegrationBlockSkeletonProps> = ({
  count = 2,
}) => {
  return (
    <div className="space-y-6">
      {/* Stat Cards Skeleton Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="flex p-4 rounded-lg border border-gray-200 dark:border-neutral-700 items-center justify-between h-[114px] bg-white dark:bg-neutral-800 animate-pulse shadow-sm"
          >
            <div className="flex-1 space-y-3">
              <div className="w-3/4 h-3 bg-gray-100 dark:bg-neutral-600 rounded shadow-inner animate-pulse"></div>
              <div className="w-1/2 h-8 bg-gray-100 dark:bg-neutral-600 rounded shadow-inner animate-pulse"></div>
            </div>
            <div className="w-12 h-12 rounded-full bg-gray-50 dark:bg-neutral-800 flex items-center justify-center">
              <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-neutral-600 animate-pulse"></div>
            </div>
          </div>
        ))}
      </div>

      {/* Integration Cards Grid Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            className={`flex flex-col border rounded-2xl shadow-md overflow-hidden bg-white dark:bg-neutral-800 animate-pulse relative ${index === 0 ? 'bg-amber-50/20 border-amber-100' : 'border-gray-200 dark:border-neutral-700'}`}
          >
            {index === 0 && (
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 h-4 w-24 bg-amber-100/50 rounded-full animate-pulse" />
            )}
            {/* Header Skeleton */}
            <div className="px-4 py-2 border-b border-gray-50 bg-gray-100 dark:bg-neutral-600 flex items-center justify-between">
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <div className="h-4 w-3/4 bg-gray-200 dark:bg-neutral-700 rounded" />
                <div className="h-3 w-1/2 bg-gray-200 dark:bg-neutral-700 rounded" />
              </div>
              <div className="flex items-center gap-1 ml-2">
                <div className="w-7 h-7 bg-gray-200 dark:bg-neutral-700 rounded-lg" />
                <div className="w-7 h-7 bg-gray-200 dark:bg-neutral-700 rounded-lg" />
                <div className="w-7 h-7 bg-gray-200 dark:bg-neutral-700 rounded-lg" />
              </div>
            </div>

            {/* Body Skeleton */}
            <div className="p-4 bg-gray-50 dark:bg-neutral-800/30">
              <div className="flex items-center justify-center gap-4">
                {/* Spend Box Skeleton */}
                <div className="border border-gray-100 dark:border-neutral-700 p-3 rounded-lg flex items-center gap-2 w-fit relative min-w-[60px] min-h-[60px] bg-white dark:bg-neutral-800 shadow-sm">
                  <div className="w-11 h-11 bg-gray-50 dark:bg-neutral-800 rounded-xl animate-pulse" />
                  <div className="absolute left-2 -top-[10px] h-3 w-10 bg-white dark:bg-neutral-800 border border-gray-50 rounded-full" />
                </div>

                {/* Arrow Skeleton */}
                <div className="w-7 h-7 bg-white dark:bg-neutral-800 rounded-full border border-gray-50 shadow-sm flex items-center justify-center">
                  <div className="w-3 h-0.5 bg-gray-200 dark:bg-neutral-700 rounded" />
                </div>

                {/* Revenue Box Skeleton */}
                <div className="border border-gray-100 dark:border-neutral-700 p-3 rounded-lg flex items-center gap-2 w-fit relative min-w-[100px] min-h-[60px] bg-white dark:bg-neutral-800 shadow-sm">
                  <div className="w-11 h-11 bg-gray-50 dark:bg-neutral-800 rounded-xl animate-pulse" />
                  <div className="w-11 h-11 bg-gray-50 dark:bg-neutral-800 rounded-xl animate-pulse" />
                  <div className="absolute left-2 -top-[10px] h-3 w-14 bg-white dark:bg-neutral-800 border border-gray-50 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        ))}
        {/* Connect Another Pair Placeholder Skeleton */}
        <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-100 dark:border-neutral-700 rounded-2xl p-6 min-h-[140px] animate-pulse">
          <div className="h-10 w-10 rounded-full bg-gray-50 dark:bg-neutral-800 flex items-center justify-center mb-3">
            <div className="w-5 h-5 bg-gray-100 dark:bg-neutral-600 rounded-full" />
          </div>
          <div className="h-4 w-32 bg-gray-50 dark:bg-neutral-800 rounded" />
        </div>
      </div>

      {/* Recent Activity Logs Skeleton */}
      <Card className="border-gray-50 shadow-sm mt-8 animate-pulse">
        <div className="flex items-center justify-between mb-6">
          <div className="h-5 w-48 bg-gray-100 dark:bg-neutral-600 rounded" />
          <div className="h-4 w-16 bg-gray-100 dark:bg-neutral-600 rounded" />
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center justify-between border-b border-gray-50 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-gray-50 dark:bg-neutral-800 rounded" />
                <div className="h-4 w-40 bg-gray-50 dark:bg-neutral-800 rounded" />
              </div>
              <div className="h-4 w-32 bg-gray-50 dark:bg-neutral-800 rounded" />
              <div className="h-4 w-20 bg-gray-50 dark:bg-neutral-800 rounded ml-10" />
              <div className="h-6 w-20 bg-gray-50 dark:bg-neutral-800 rounded-full ml-10" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default IntegrationBlockSkeleton;
